import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Typography } from 'src/components/Typography';
import { checkPayment } from 'src/models/cart/cart';

import { PaymentStep, usePaymentDialogContext } from './PaymentDialogContext';

const PaymentComplete: React.FC<{
  onPaymentDialogClose: () => void;
  onPaymentComplete?: () => void;
}> = ({ onPaymentDialogClose, onPaymentComplete }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.paymentDialog',
  });
  const { savedCart, setCartError, setCurrentStep } = usePaymentDialogContext();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const intervalIdRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (!savedCart?.id) return;

    let mounted = true;

    const checkStatus = async () => {
      try {
        const data = await checkPayment({
          setup_intent: savedCart.setupIntentId || '',
          setup_intent_secret: savedCart.setupIntentSecret || '',
          payment_intent: savedCart.paymentIntentId || '',
          payment_intent_secret: savedCart.paymentIntentSecret || '',
          cart_id: savedCart.id,
        });

        if (!mounted) return;

        switch (data.status) {
          case 'succeeded':
            clearInterval(intervalIdRef.current);
            setLoading(false);
            setSuccess(true);
            if (onPaymentComplete) {
              onPaymentComplete();
            }
            setTimeout(() => {
              onPaymentDialogClose();
            }, 500);
            break;
          case 'incomplete':
            clearInterval(intervalIdRef.current);
            setLoading(false);
            break;
          case 'error':
            clearInterval(intervalIdRef.current);
            setLoading(false);
            setCartError(data.error_message);
            setCurrentStep(PaymentStep.Confirmation);
            break;
        }
      } catch (error) {
        if (!mounted) return;
        clearInterval(intervalIdRef.current);
        setLoading(false);
        setCartError(t('unknownPaymentError'));
        setCurrentStep(PaymentStep.Confirmation);
      }
    };

    if (!intervalIdRef.current && !success && loading) {
      // Start checking immediately
      checkStatus();
      // Then check every second
      intervalIdRef.current = setInterval(checkStatus, 1000);
    }

    return () => {
      mounted = false;
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = undefined;
    };
  }, [
    loading,
    onPaymentComplete,
    onPaymentDialogClose,
    savedCart?.id,
    savedCart?.paymentIntentId,
    savedCart?.paymentIntentSecret,
    savedCart?.setupIntentId,
    savedCart?.setupIntentSecret,
    setCartError,
    setCurrentStep,
    success,
    t,
  ]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        p={4}
      >
        <CircularProgress size="medium" />
        <Typography align="center" variant="h6">
          {t('processingPayment')}
        </Typography>
        <Typography align="center" color="text.secondary">
          {t('doNotCloseModal')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
      p={4}
    >
      <Typography variant="h6" color="success.main">
        {success ? t('paymentSuccessful') : t('checkoutComplete')}
      </Typography>
      <Typography align="center" color="text.secondary">
        {success ? t('itemsBeingProcessedYours') : t('itemsBeingProcessed')}
      </Typography>
    </Box>
  );
};

export default PaymentComplete;
